import React, { useEffect, useState } from "react";
import {
    Layout,
    Typography,
    Breadcrumb,
    Spin,
    Select,
    DatePicker,
    Table,
    Row,
    Col,
    Form,
    Card,
    Button,
    Tag,
    Statistic,
    Space
} from "antd";
import Headerdiv from "../../layout/header.js";
import Footerdiv from "../../layout/footer.js";
import { APPBACKGROUND_COLOR, MAINAPIURL } from "../../appsettings.js";
import axios from "axios";
import { AlertError } from "../../usableclasses.js";
import { Link } from "react-router-dom";
import moment from "moment";
import { PDFDownloadLink } from '@react-pdf/renderer';
import { FilePdfOutlined, FileExcelOutlined, ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { TellerSummaryPDF } from './teller-summery-pdf.js';
import { handleTellerSummaryExportToExcel } from './teller-summery-excel.js';

const { Content } = Layout;
const { Option } = Select;

const TellerSummary = () => {
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");

    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();

    const [dataSource, setDataSource] = useState([]);
    const [erpUsers, setErpUsers] = useState([]);

    const [printTeller, setPrintteller] = useState("");
    const [printTransdate, setPrinttransdate] = useState("");
    const [printTranstype, setPrinttranstype] = useState("");

    useEffect(() => {
        fetchErpUsers();
        form.setFieldsValue({
            targetDate: undefined, // Do not preselect date
            paidType: undefined
        });
    }, []);

    const fetchTellerSummary = async (payload) => {
        try {
            setIsLoading(true);
            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.post(`${ApiURL}/reports/teller/gettellertransactionsummary`, payload, { headers });
            setDataSource(res?.data || []);
        } catch (error) {
            AlertError("Failed to Load Data");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchErpUsers = async () => {
        try {
            const headers = { SystemAccessToken: TOKEN };
            const res = await axios.get(`${ApiURL}/user/listfordropdown`, { headers });
            setErpUsers(res.data);
        } catch (error) {
            AlertError("Failed to Load ERP Users");
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'SUBMITTED': return 'cyan';
            case 'UNPAID': return 'orange';
            case 'PAYMENT FAILED': return 'volcano';
            case 'PAID': return 'green';
            case 'PROCESSING': return 'blue';
            case 'ORDER READY': return 'purple';
            case 'COMPLETED': return 'geekblue';
            case 'CANCELLED':
            case 'CANCELED': return 'red';
            default: return 'default';
        }
    };

    const onFinish = (values) => {
        const payload = {
            tellerId: parseInt(values.erpUserId),
            selectedDate: values.targetDate ? values.targetDate.format("YYYY-MM-DD") : "",
            transactionType: values.paidType
        };
        fetchTellerSummary(payload);

        setPrintteller(erpUsers.find(user => user.id === values.erpUserId).label);
        setPrinttransdate(values.targetDate ? values.targetDate.format("YYYY-MM-DD") : "");
        setPrinttranstype(values.paidType);
    };

    const columns = [
        { title: "Order No", dataIndex: "invoiceNo", key: "invoiceNo" },
        {
            title: "Current Status",
            dataIndex: "invoiceStatus",
            key: "invoiceStatus",
            render: (text) => <Tag color={getStatusColor(text)}>{text}</Tag>
        },
        { title: "Order Total", dataIndex: "orderCost", key: "orderCost" },
        { title: "Paid Type", dataIndex: "transactionType", key: "transactionType" },
        { title: "Paid Amount", dataIndex: "transactionAmount", key: "transactionAmount" },
        { title: "Payment Details", dataIndex: "transactionDetail", key: "transactionDetail" },
        { title: "Transaction Date/Time", dataIndex: "transactionTime", key: "transactionTime" }
    ];

    // Summary calculations
    const totalPaidAmount = dataSource.reduce((sum, item) => sum + (item.transactionAmount || 0), 0);

    return (
        <Spin spinning={isLoading} tip="Processing...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: "10px" }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item><Link to={"/reports"}>Reports</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Teller Daily Transactions Summary Report</Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: 20 }} title={
                        <Row align="middle" justify="space-between">
                            <Col span={12}>
                                <Typography.Title level={3} style={{ textAlign: 'left', margin: 0, fontSize: 22 }}>Teller Daily Transactions Summary</Typography.Title>
                            </Col>
                            <Col span={12} style={{ textAlign: 'right' }}>
                                <Space>
                                    <PDFDownloadLink
                                        document={<TellerSummaryPDF data={dataSource} tellername={printTeller} filterdate={printTransdate} transactiontype={printTranstype} />}
                                        fileName={`Teller_Summary_${new Date().toISOString().slice(0, 10)}.pdf`}
                                    >
                                        <Button
                                            disabled={dataSource.length === 0}
                                            icon={<FilePdfOutlined />}
                                            type="primary"
                                            style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                                        >

                                            Export to PDF
                                        </Button>
                                    </PDFDownloadLink>
                                    <Button
                                        icon={<FileExcelOutlined />}
                                        type="primary"
                                        style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
                                        onClick={() => handleTellerSummaryExportToExcel(dataSource, setIsLoading)}
                                        disabled={dataSource.length === 0}
                                    >
                                        Export to Excel
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    }>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            style={{ marginBottom: 16 }}
                        >
                            <Row gutter={16} align="bottom" justify="space-evenly">
                                <Col xs={24} sm={8} md={4} style={{ display: 'flex', alignItems: 'end' }}>
                                    <Form.Item
                                        label="ERP User"
                                        name="erpUserId"
                                        rules={[{ required: true, message: "Please select a teller" }]}
                                        style={{ width: '100%', marginBottom: 0 }}
                                    >
                                        <Select placeholder="Select Teller">
                                            {erpUsers.map(user => (
                                                <Option key={user.id} value={user.id}>{user.label}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8} md={4} style={{ display: 'flex', alignItems: 'end' }}>
                                    <Form.Item
                                        label="Target Date"
                                        name="targetDate"
                                        rules={[{ required: true, message: "Please select a date" }]}
                                        style={{ width: '100%', marginBottom: 0 }}
                                    >
                                        <DatePicker style={{ width: '100%' }} allowClear={false} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8} md={4} style={{ display: 'flex', alignItems: 'end' }}>
                                    <Form.Item
                                        label="Paid Type"
                                        name="paidType"
                                        placeholder="Please Select Paid Type"
                                        rules={[{ required: true, message: "Please select a type" }]}
                                        style={{ width: '100%', marginBottom: 0 }}
                                    >
                                        <Select>
                                            <Option value="ALL">ALL</Option>
                                            <Option value="CASH">CASH</Option>
                                            <Option value="POS">POS</Option>
                                            <Option value="TRANSFER">TRANSFER</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8} md={4} style={{ display: 'flex', alignItems: 'end' }}>
                                    <Button icon={<SearchOutlined/>} type="primary" htmlType="submit" style={{ width: '100%' }}>Get Data</Button>
                                </Col>
                                <Col xs={24} sm={8} md={4} style={{ display: 'flex', alignItems: 'end' }}>
                                    <Button
                                    icon={<ClearOutlined />}
                                        style={{ width: '100%' }}
                                        onClick={() => {
                                            form.resetFields();
                                            setDataSource([]);
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                    <Card title="Teller Transactions" style={{ marginBottom: 20 }}>
                        <Table
                            columns={columns}
                            dataSource={dataSource}
                            rowKey={record => record.id || `${record.invoiceNo}-${record.transactionDateTime}`}
                            bordered
                            pagination={false}
                        />
                    </Card>

                    {dataSource.length > 0 && (
                        <Card title="Summary" style={{ marginTop: 20 }}>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Statistic title="Total Transactions" value={dataSource.length} />
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Statistic title="Total Transactions Value" value={totalPaidAmount} prefix="Rs." precision={2} />
                                </Col>
                            </Row>
                        </Card>
                    )}
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default TellerSummary;
